import React from 'react';
import {createPortal} from 'react-dom';
import {useFloating, offset, flip, shift, autoUpdate} from '@floating-ui/react';
import {Menu, MenuButton, MenuItems} from '@headlessui/react';
import {ChevronDownIcon} from '@heroicons/react/24/outline';
import {classNames} from 'utils';
import {InputCheckbox} from 'components';

type ItemType = {
  id: string;
  label: string;
  onClick: () => void;
};

interface IDropdownMultiProps {
  title: string;
  items: ItemType[];
  selected: ItemType[];
  limitHeight?: boolean;
}

const DropdownMulti = ({title, items, selected, limitHeight = false}: IDropdownMultiProps) => {
  const {x, y, refs, strategy} = useFloating({
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });
  const selectedIds = selected.map(s => s.id);
  const prefix = (() => {
    if (selected.length === 0) return 'No';
    return `${selected.length}/${items.length}`;
  })();
  return (
    <Menu as="div" className="relative inline-block">
      <MenuButton
        ref={refs.setReference}
        className={classNames(
          'whitespace-nowrap inline-flex w-full justify-center items-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold shadow-xs inset-ring-1 inset-ring-gray-300 dark:inset-ring-slate-400',
          items.length === 0
            ? 'bg-slate-100 dark:bg-slate-500 text-slate-400 dark:text-slate-200'
            : 'bg-white dark:bg-slate-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-slate-600 cursor-pointer',
        )}
        disabled={items.length === 0}>
        {prefix} {title}
        <ChevronDownIcon aria-hidden="true" className="-mr-1 size-5 text-gray-400" />
      </MenuButton>
      {createPortal(
        <MenuItems
          transition
          ref={refs.setFloating}
          className={classNames(
            'absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white dark:bg-slate-800 shadow-lg outline-1 outline-black/5 dark:outline-slate-400 transition data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in',
            limitHeight && 'max-h-36 overflow-scroll no-scrollbar',
          )}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            zIndex: 50,
          }}>
          <div className="py-1">
            {items.map(item => {
              const selected = selectedIds.includes(item.id);
              return (
                <div
                  key={item.id}
                  className={classNames(
                    'px-4',
                    'hover:bg-gray-100 hover:outline-hidden dark:hover:bg-slate-700',
                    selected && 'bg-gray-100 dark:bg-slate-700',
                  )}>
                  <InputCheckbox checked={selected} onChange={item.onClick}>
                    {({id}) => {
                      return (
                        <label
                          htmlFor={id}
                          className={classNames('w-full -mr-4 py-2 text-sm/6 text-gray-900 dark:text-white cursor-pointer select-none')}>
                          {item.label}
                        </label>
                      );
                    }}
                  </InputCheckbox>
                </div>
              );
            })}
          </div>
        </MenuItems>,
        document.body,
      )}
    </Menu>
  );
};

export default React.memo(DropdownMulti);
